import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { openSidebar, resetSidebar, setSidebarContent } from '@/redux/sidebar/actions';
import { selectOpenSidebar, selectSidebarMeta } from '@/redux/sidebar/selectors';
import { SidebarOptions } from '@/shared/types/sidebar';

type SidebarMeta = Record<string, string>;
type SidebarName = Exclude<SidebarOptions, null>;

/**
 * One-stop hook for the Redux-backed sidebar: read state, open/close, and
 * check whether a specific content is active. `open` / `close` have stable
 * identity; `content` / `meta` subscribe via `useSelector`, so components
 * only re-render when their slice actually changes.
 */
export const useSidebar = () => {
  const dispatch = useDispatch();
  const content = useSelector(selectOpenSidebar);
  const meta = useSelector(selectSidebarMeta);

  const open = useCallback(
    (next: SidebarName, metadata?: SidebarMeta) => {
      dispatch(openSidebar({ content: next, meta: metadata }));
    },
    [dispatch]
  );

  const close = useCallback(() => {
    dispatch(setSidebarContent(null));
  }, [dispatch]);

  /** For a page tearing down a panel it owns. `close` is for the user closing it. */
  const reset = useCallback(() => {
    dispatch(resetSidebar());
  }, [dispatch]);

  const isOpen = useCallback(
    (which?: SidebarOptions | SidebarOptions[]) => {
      if (content == null) {
        return false;
      }
      if (which == null) {
        return true;
      }
      return Array.isArray(which) ? which.includes(content) : which === content;
    },
    [content]
  );

  return { content, meta, open, close, reset, isOpen };
};
